import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { MasonryGrid } from "../../../components/MasonryGrid";
import { IProduct } from "../../../models/catalogue";
import { useAppSelector } from "../../../store/reducers";
import { ProductCard } from "../ProductCard";
import { GridFilterConfig } from "../filters";
import { GridVariant, IProductCardType } from "../models";
import {
    filterProducts,
    getActiveSorter,
    selectFilters,
    selectProducts,
    selectSortMethod,
} from "../selectors";
import { GridSorterConfig } from "../sorters";
import { BuildGridTile, GRID_TILE_CLASS, IGridTileProps } from "../tiles";

import styles from "./index.module.scss";

type IProps = Immutable<{
    gridVariant: GridVariant;
    cardType: IProductCardType;
    filters: readonly GridFilterConfig[];
    sorters?: readonly GridSorterConfig[];
    disableHoverOverlay?: boolean;
}> & {
    buildExternalCallout?: (rootProduct: IProduct) => React.ReactNode;
    /**
     * Renders the tile for each product instead of `ProductCard`. When set,
     * `cardType`, `disableHoverOverlay` and `buildExternalCallout` reach
     * nothing for a product the tile draws itself -- but they still configure
     * the card `renderDefault` produces, so they stay live for any product
     * the tile hands back.
     */
    buildTile?: BuildGridTile;
};

export const GridContent = (props: IProps): React.ReactNode => {
    /**
     * One `IGridTileProps` per tile, for the lifetime of the grid.
     *
     * A custom tile is typically `React.memo`'d, and this bag is one of its
     * props -- built fresh each render it would fail every shallow compare, so
     * the memo would never hit and every tile would re-render on every filter
     * and sort. Both fields are already stable (the class is a constant,
     * `MasonryGrid` keeps one ref per tile ID), so keying the wrapper on the
     * ref is enough to make it stable too.
     *
     * A plain `Map`: a `WeakMap` would suggest entries are released when a
     * tile leaves the grid, and they are not -- `MasonryGrid` holds every ref
     * it has ever created in a `Map` it never deletes from, so the keys stay
     * reachable either way. Worth revisiting only if that changes.
     */
    const tileProps = React.useRef(
        new Map<React.RefObject<HTMLDivElement | null>, IGridTileProps>(),
    );
    const getTileProps = (
        ref: React.RefObject<HTMLDivElement | null>,
    ): IGridTileProps => {
        const cached = tileProps.current.get(ref);
        if (cached) {
            return cached;
        }
        const built: IGridTileProps = {
            ref: ref,
            className: GRID_TILE_CLASS,
        };
        tileProps.current.set(ref, built);
        return built;
    };

    const allProducts = useAppSelector(selectProducts);
    const filterValues = useAppSelector(selectFilters);
    const sortMethod = useAppSelector(selectSortMethod);

    const products = filterProducts(props.filters, allProducts, filterValues);
    const sorter = getActiveSorter(
        props.sorters ? props.sorters : undefined,
        sortMethod,
    );
    const sortedProducts = sorter.sort(products);

    console.table(sortedProducts.map((p) => ({ title: p.title })));

    return (
        <MasonryGrid
            className={styles.container}
            itemSelector={`.${GRID_TILE_CLASS}`}
            entities={sortedProducts}
            getTileID={(product) => `${product.id}`}
            buildTile={(product, ref) => {
                const renderDefault = () => (
                    <ProductCard
                        forwardedRef={ref}
                        gridVariant={props.gridVariant}
                        cardType={props.cardType}
                        rootProduct={product}
                        disableHoverOverlay={props.disableHoverOverlay}
                        buildExternalCallout={props.buildExternalCallout}
                    />
                );
                return props.buildTile
                    ? props.buildTile(product, getTileProps(ref), renderDefault)
                    : renderDefault();
            }}
            emptyState={
                <div>
                    <p className={styles.emptyMessageHeading}>
                        {t`WE CAN'T FIND A PRODUCT THAT MATCHES YOUR SEARCH.`}
                    </p>
                    <p className={styles.emptyMessageBody}>
                        {t`Sorry about that! Please try your search again with another set of filters.`}
                    </p>
                </div>
            }
            options={{
                horizontalOrder: true,
                gutter: 0,
            }}
            forceEqualHeight={true}
        />
    );
};
